import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { MerchantView, UserView } from '@/api/types';
import { Card, ErrorNote, Spinner, Timestamp } from '@/components/Common';
import { Modal } from '@/components/Modal';

const ROLES = ['ADMIN', 'OPS', 'FINANCE', 'SUPPORT', 'VIEWER'] as const;

export function UsersPage() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<UserView | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ users: UserView[] }>('/api/admin/users'),
  });
  const { data: merchantsData } = useQuery({
    queryKey: ['merchants'],
    queryFn: () => api.get<{ merchants: MerchantView[] }>('/api/admin/merchants'),
  });
  const merchantById = new Map((merchantsData?.merchants ?? []).map((m) => [m.id, m]));

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/api/admin/users/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  return (
    <div className="space-y-4">
      <Card
        title={`Console users — ${data?.users.length ?? 0}`}
        actions={
          <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
            Add user
          </button>
        }
      >
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-ink-200">
            <tr>
              <th className="th">Email</th><th className="th">Name</th><th className="th">Role</th>
              <th className="th">Scope</th><th className="th">Last sign-in</th>
              <th className="th">Active</th><th className="th" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {(data?.users ?? []).map((user) => (
              <tr key={user.id} className={user.active ? '' : 'bg-ink-50 text-ink-400'}>
                <td className="td">
                  {user.email}
                  {user.mustChangePassword && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800"
                          title="Still using the password set for them">
                      pending
                    </span>
                  )}
                </td>
                <td className="td">{user.fullName}</td>
                <td className="td">
                  <select
                    className="rounded border border-ink-300 bg-white px-1.5 py-0.5 text-xs"
                    value={user.role}
                    onChange={(e) => update.mutate({ id: user.id, body: { role: e.target.value } })}
                  >
                    {ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
                  </select>
                </td>
                <td className="td text-xs text-ink-500">
                  {user.merchantId
                    ? `${merchantById.get(user.merchantId)?.code ?? 'Merchant-scoped'}${
                        merchantById.get(user.merchantId)?.accountType
                        && merchantById.get(user.merchantId)!.accountType !== 'GENERAL'
                          ? ` (${merchantById.get(user.merchantId)!.accountType})` : ''
                      }`
                    : 'Platform-wide'}
                </td>
                <td className="td"><Timestamp value={user.lastLoginAt} /></td>
                <td className="td">
                  <button
                    type="button"
                    className={`rounded px-2 py-0.5 text-[11px] font-semibold ${
                      user.active ? 'bg-emerald-100 text-emerald-800' : 'bg-ink-200 text-ink-600'
                    }`}
                    onClick={() => update.mutate({ id: user.id, body: { active: !user.active } })}
                  >
                    {user.active ? 'ACTIVE' : 'DISABLED'}
                  </button>
                </td>
                <td className="td text-right">
                  <button type="button" className="btn-secondary" onClick={() => setResetting(user)}>
                    Reset password
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
          VIEWER and FINANCE see masked target numbers. SUPPORT, OPS and ADMIN see them in full, and
          each reveal is written to the audit log.
        </p>
      </Card>

      {update.isError && <ErrorNote error={update.error} />}
      {creating && <CreateUserModal onClose={() => setCreating(false)} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}

/** A random password this console generates and sets directly — not emailed, since there's no
 * mail sender configured — shown once for the admin to relay to the user out of band, the same
 * way a newly created user's starting password is handed over today. */
function generateTempPassword(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 20);
}

function ResetPasswordModal({ user, onClose }: { user: UserView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [password] = useState(generateTempPassword);
  const [copied, setCopied] = useState(false);

  const reset = useMutation({
    mutationFn: () => api.patch(`/api/admin/users/${user.id}`, { password }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  async function copy() {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      // Clipboard access can be denied — the password is still selectable text either way.
    }
  }

  return (
    <Modal title={`Reset password — ${user.email}`} onClose={onClose}>
      {!reset.isSuccess ? (
        <>
          <p className="text-sm text-ink-600">
            This sets a new starting password for <strong>{user.email}</strong> and forces them to
            choose their own on next sign-in, same as when an account is first created.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary" disabled={reset.isPending}
                    onClick={() => reset.mutate()}>
              {reset.isPending ? 'Resetting…' : 'Reset password'}
            </button>
          </div>
          {reset.isError && <ErrorNote error={reset.error} />}
        </>
      ) : (
        <>
          <p className="text-sm text-ink-600">
            Share this with {user.fullName} directly — it's shown only once and won't be
            recoverable from this screen afterwards.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <code className="mono flex-1 rounded border border-ink-200 bg-ink-50 px-3 py-2 text-sm">
              {password}
            </code>
            <button type="button" className="btn-secondary" onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="button" className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </>
      )}
    </Modal>
  );
}

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    email: '', fullName: '', password: '', role: 'VIEWER' as (typeof ROLES)[number], merchantId: '',
  });

  const { data: merchantsData } = useQuery({
    queryKey: ['merchants'],
    queryFn: () => api.get<{ merchants: MerchantView[] }>('/api/admin/merchants'),
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/admin/users', {
      ...form,
      merchantId: form.merchantId || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  const passwordTooShort = form.password.length > 0 && form.password.length < 12;

  return (
    <Modal title="Add a console user" onClose={onClose}>
      <div className="space-y-3">
        <label className="block">
          <span className="label">Email</span>
          <input className="input mt-1" type="email" value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label className="block">
          <span className="label">Full name</span>
          <input className="input mt-1" value={form.fullName}
                 onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
        </label>
        <label className="block">
          <span className="label">Password</span>
          <input className="input mt-1" type="password" value={form.password}
                 onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <span className={`mt-1 block text-xs ${passwordTooShort ? 'text-red-700' : 'text-ink-500'}`}>
            At least 12 characters. Just a starting password — they'll be asked to set their own
            the first time they sign in.
          </span>
        </label>
        <label className="block">
          <span className="label">Role</span>
          <select className="input mt-1" value={form.role}
                  onChange={(e) => setForm({
                    ...form, role: e.target.value as (typeof ROLES)[number],
                  })}>
            {ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label">Merchant scope</span>
          <select className="input mt-1" value={form.merchantId}
                  onChange={(e) => setForm({ ...form, merchantId: e.target.value })}>
            <option value="">Platform-wide (no merchant)</option>
            {(merchantsData?.merchants ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.code} — {m.name}{m.accountType !== 'GENERAL' ? ` (${m.accountType})` : ''}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-ink-500">
            Pick the vendor or company this user belongs to, so they only see that account's
            selling screen or recharges — or leave platform-wide for staff.
          </span>
        </label>
      </div>

      {create.isError && <ErrorNote error={create.error} />}

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary"
                disabled={create.isPending || !form.email || !form.fullName
                          || form.password.length < 12}
                onClick={() => create.mutate()}>
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
      </div>
    </Modal>
  );
}
